import { useEffect } from 'react'
import { systemsToGeoJSON, SYSTEM_COLORS } from '../../utils/weather'

// Weather: (1) merge the live weather snapshot into feature properties so the
// weather layer expression (['get','weather_type']) and hover popups can read
// it; and (2) the weather-systems overlay (masses d'air) — translucent circles
// shown only while the weather layer is active (depressions blue, anticyclones
// orange).
export function useWeather(map, { weatherData, weatherLayerActive, planMode, regionsData, regionsMercatorData, regionsGlobeData }) {
  // (1) Merge weather snapshot into both projection datasets, push to source.
  useEffect(() => {
    if (!map.current || !regionsData || !map.current.getSource('regions')) return
    const regionWeather = weatherData?.regions
    if (!regionWeather || Object.keys(regionWeather).length === 0) return

    const applyTo = (dataset) => {
      if (!dataset) return
      dataset.features.forEach((feature) => {
        const id = feature.properties.region_id
        const w = regionWeather[id] ?? regionWeather[String(id)]
        if (!w) return
        const p = feature.properties
        p.weather_type = w.weather_type
        p.weather_temp = w.temperature
        p.weather_wind = w.wind_speed
      })
    }

    applyTo(regionsMercatorData)
    applyTo(regionsGlobeData)

    const source = map.current.getSource('regions')
    if (source) {
      source.setData({ type: regionsData.type, features: regionsData.features })
    }
  }, [map, weatherData, regionsData, regionsMercatorData, regionsGlobeData])

  // (2) Weather-systems overlay. Gated on regions-fill existing (NOT
  // isStyleLoaded(), which flickers false constantly with the raster satellite
  // source + large GeoJSON updates); getLayer('regions-fill') is sufficient
  // proof the map is ready, and add-order keeps the opaque regions-fill from
  // covering the circles underneath.
  useEffect(() => {
    if (!map.current || !map.current.getLayer('regions-fill')) return
    const showSystems = !!weatherLayerActive && !planMode
    const fc = systemsToGeoJSON(showSystems ? (weatherData?.systems || []) : [])

    const src = map.current.getSource('weather-systems')
    if (!src) {
      if (!map.current.getSource('weather-systems')) {
        map.current.addSource('weather-systems', { type: 'geojson', data: fc })
      }
      map.current.addLayer({
        id: 'weather-systems-fill',
        type: 'fill',
        source: 'weather-systems',
        paint: {
          'fill-color': [
            'match', ['get', 'kind'],
            'depression', SYSTEM_COLORS.depression,
            'anticyclone', SYSTEM_COLORS.anticyclone,
            '#888888',
          ],
          'fill-opacity': ['interpolate', ['linear'], ['get', 'intensity'], 0, 0.05, 1, 0.28],
        },
      })
      map.current.addLayer({
        id: 'weather-systems-line',
        type: 'line',
        source: 'weather-systems',
        paint: {
          'line-color': [
            'match', ['get', 'kind'],
            'depression', SYSTEM_COLORS.depression,
            'anticyclone', SYSTEM_COLORS.anticyclone,
            '#888888',
          ],
          'line-width': 1.5,
          'line-opacity': 0.7,
        },
      })
    } else {
      src.setData(fc)
    }
  }, [map, weatherData, weatherLayerActive, planMode, regionsData])
}
